// ==================== MODERN HEADER WITH GRADIENTS ====================

import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Switch,
  FormControlLabel,
  alpha,
} from '@mui/material';
import { Menu as MenuIcon, LogOut, Moon, Sun } from 'lucide-react';
import { styled } from '@mui/material/styles';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';


const Header = ({ onSidebarToggle, height }) => {
  const { user, logout } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleProfileMenuOpen = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleProfileMenuClose();
    logout();
  };

  return (
    <AppBar
      position="static"
      elevation={0}
      sx={{
        background: isDarkMode
          ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.95) 100%)'
          : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
        backdropFilter: 'blur(20px)',
        borderBottom: isDarkMode
          ? '1px solid rgba(148, 163, 184, 0.1)'
          : '1px solid rgba(148, 163, 184, 0.2)',
        color: isDarkMode ? '#f1f5f9' : '#1e293b',
        height: height,
        boxShadow: isDarkMode
          ? '0 4px 20px rgba(0, 0, 0, 0.3)'
          : '0 4px 20px rgba(0, 0, 0, 0.08)',
      }}
    >
      <Toolbar
        sx={{
          height: height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: { xs: 2, sm: 3 },
        }}
      >
        {/* Left Section */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            edge="start"
            onClick={onSidebarToggle}
            sx={{
              mr: 2,
              color: '#3b82f6',
              background:
                'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%)',
              border: '1px solid rgba(59, 130, 246, 0.2)',
              '&:hover': {
                background:
                  'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%)',
                transform: 'scale(1.05)',
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            <MenuIcon size={20} />
          </IconButton>

          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              display: { xs: 'none', sm: 'block' },
            }}
          >
            Certificate Management System
          </Typography>
        </Box>

        {/* Right Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Theme Toggle */}
          <IconButton
            onClick={toggleTheme}
            sx={{
              color: isDarkMode ? '#94a3b8' : '#64748b',
              background: isDarkMode
                ? 'linear-gradient(135deg, rgba(251, 191, 36, 0.1) 0%, rgba(251, 191, 36, 0.05) 100%)'
                : 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(139, 92, 246, 0.05) 100%)',
              border: isDarkMode
                ? '1px solid rgba(251, 191, 36, 0.2)'
                : '1px solid rgba(139, 92, 246, 0.2)',
              '&:hover': {
                background: isDarkMode
                  ? 'linear-gradient(135deg, rgba(251, 191, 36, 0.2) 0%, rgba(251, 191, 36, 0.1) 100%)'
                  : 'linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(139, 92, 246, 0.1) 100%)',
                color: isDarkMode ? '#fbbf24' : '#8b5cf6',
                transform: 'scale(1.05)',
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
          </IconButton>

          {/* User Profile */}
          <IconButton onClick={handleProfileMenuOpen} sx={{ p: 0.5, ml: 1 }}>
            <Avatar
              sx={{
                width: 36,
                height: 36,
                background:
                  user?.role === 'admin'
                    ? 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)'
                    : 'linear-gradient(135deg, #06b6d4 0%, #10b981 100%)',
                fontSize: '0.875rem',
                fontWeight: 600,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                border: '2px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              {user?.FirstName?.charAt(0) || 'U'}
            </Avatar>
          </IconButton>
        </Box>

        {/* Profile Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleProfileMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{
            sx: {
              mt: 1,
              borderRadius: 3,
              minWidth: 240,
              background: isDarkMode
                ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.95) 100%)'
                : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
              backdropFilter: 'blur(20px)',
              border: isDarkMode
                ? '1px solid rgba(148, 163, 184, 0.1)'
                : '1px solid rgba(148, 163, 184, 0.2)',
              boxShadow: isDarkMode
                ? '0 20px 25px rgba(0, 0, 0, 0.4)'
                : '0 20px 25px rgba(0, 0, 0, 0.1)',
            },
          }}
        >
          {/* User Info */}
          <Box sx={{ px: 3, py: 2 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                color: isDarkMode ? '#f1f5f9' : '#1e293b',
              }}
            >
              {user?.FirstName} {user?.LastName}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: isDarkMode ? '#94a3b8' : '#64748b' }}
            >
              {user?.email}
            </Typography>
            <Box
              sx={{
                display: 'inline-block',
                mt: 1,
                px: 2,
                py: 0.5,
                borderRadius: 2,
                background:
                  user?.role === 'admin'
                    ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%)'
                    : 'linear-gradient(135deg, rgba(6, 182, 212, 0.2) 0%, rgba(16, 185, 129, 0.2) 100%)',
                border:
                  user?.role === 'admin'
                    ? '1px solid rgba(59, 130, 246, 0.3)'
                    : '1px solid rgba(6, 182, 212, 0.3)',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: user?.role === 'admin' ? '#3b82f6' : '#06b6d4',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  fontSize: '0.7rem',
                }}
              >
                {user?.role} Account
              </Typography>
            </Box>
          </Box>

          {/* Theme Toggle in Menu */}
          <MenuItem sx={{ px: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={isDarkMode}
                  onChange={toggleTheme}
                  size="small"
                  sx={{
                    '& .MuiSwitch-thumb': {
                      background:
                        'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                    },
                    '& .MuiSwitch-track': {
                      backgroundColor: isDarkMode ? '#374151' : '#d1d5db',
                    },
                  }}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {isDarkMode ? <Moon size={16} /> : <Sun size={16} />}
                  <Typography
                    variant="body2"
                    sx={{ color: isDarkMode ? '#e2e8f0' : '#475569' }}
                  >
                    {isDarkMode ? 'Dark' : 'Light'} Mode
                  </Typography>
                </Box>
              }
              sx={{ m: 0 }}
            />
          </MenuItem>

          {/* Logout */}
          <MenuItem
            onClick={handleLogout}
            sx={{
              px: 3,
              mt: 1,
              borderTop: isDarkMode
                ? '1px solid rgba(148, 163, 184, 0.1)'
                : '1px solid rgba(148, 163, 184, 0.2)',
            }}
          >
            <ListItemIcon>
              <LogOut size={18} color="#ef4444" />
            </ListItemIcon>
            <ListItemText primary="Logout" sx={{ color: '#ef4444' }} />
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default Header;
